import React from 'react';
import { motion } from 'framer-motion';
import { ArtistAction, GameState } from '@/types/career-sim';
import { cardHover, cardTap } from '@/utils/animationVariants';

interface ActionCardProps {
  action: ArtistAction;
  gameState: GameState;
  onStart: (action: ArtistAction) => void;
  disabled?: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({ action, gameState, onStart, disabled }) => {
  const canAfford = gameState.needs.energy >= action.energyCost;
  const isActive = gameState.activeAction?.id === action.id;

  // Get flavor text based on action
  const getFlavorText = () => {
    const texts: Record<string, string> = {
      'create-music': 'Lock in and make magic',
      'record-track': 'Time to lay down some heat',
      'perform-live': 'Light up the stage',
      'network': 'Connect with the culture',
      'rest': 'Recharge your batteries',
      'social-media': 'Build your online presence',
      'upgrade-studio': 'Level up your workspace',
      'collaborate': 'Create with others',
    };
    return texts[action.id] || 'Take action';
  };

  // Get outcome display
  const getOutcomes = () => {
    const outcomes = [];
    if (action.outcomes.creativity) outcomes.push(`+${action.outcomes.creativity} Creativity`);
    if (action.outcomes.mentalHealth) outcomes.push(`+${action.outcomes.mentalHealth} Mental`);
    if (action.outcomes.funds) outcomes.push(`+$${action.outcomes.funds}`);
    if (action.outcomes.fanbase) outcomes.push(`+${action.outcomes.fanbase} Fans`);
    if (action.outcomes.reputation) outcomes.push(`+${action.outcomes.reputation} Rep`);
    return outcomes;
  };

  return (
    <motion.button
      variants={cardHover}
      whileHover={!disabled && !isActive ? "hover" : undefined}
      whileTap={!disabled && !isActive ? cardTap : undefined}
      onClick={() => !disabled && !isActive && canAfford && onStart(action)}
      disabled={disabled || isActive || !canAfford}
      className={`
        relative flex-shrink-0 w-[220px] h-[280px] rounded-xl overflow-hidden
        bg-gradient-to-br from-black/80 to-black/60 backdrop-blur-md
        border-2 transition-all duration-300
        ${isActive 
          ? 'border-neon-blue shadow-neon-blue cursor-wait' 
          : canAfford && !disabled
          ? 'border-neon-purple/40 hover:border-neon-purple shadow-lg hover:shadow-neon-purple/50 cursor-pointer'
          : 'border-white/10 opacity-50 cursor-not-allowed'
        }
      `}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-neon-purple/20 via-transparent to-transparent opacity-50" />

      {/* Icon */}
      <div className="relative z-10 pt-6 pb-4 flex justify-center">
        <motion.div
          animate={isActive ? { 
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          } : {}}
          transition={{ duration: 1, repeat: isActive ? Infinity : 0 }}
          className="text-6xl"
        >
          {action.icon}
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 space-y-3">
        {/* Title */}
        <h3 className="text-lg font-bold text-white text-center leading-tight">
          {action.name}
        </h3>

        {/* Flavor text */}
        <p className="text-xs text-white/60 text-center italic">
          "{getFlavorText()}"
        </p>

        {/* Costs */}
        <div className="flex items-center justify-center gap-2 text-xs">
          <div className={`px-2 py-1 rounded ${canAfford ? 'bg-red-500/20 text-red-300' : 'bg-red-500/40 text-red-200'}`}>
            -{action.energyCost} Energy
          </div>
          {action.fundsCost > 0 && (
            <div className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-300">
              -${action.fundsCost}
            </div>
          )}
        </div>

        {/* Outcomes */}
        <div className="space-y-1">
          {getOutcomes().map((outcome, i) => (
            <div key={i} className="text-xs text-green-400 text-center">
              {outcome}
            </div>
          ))}
        </div>

        {/* Duration */}
        <div className="text-xs text-white/40 text-center">
          {action.duration}s duration
        </div>
      </div>

      {/* Active indicator */}
      {isActive && (
        <motion.div
          className="absolute bottom-4 left-1/2 -translate-x-1/2"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <div className="px-3 py-1 bg-neon-blue/30 rounded-full text-xs text-white border border-neon-blue">
            In Progress...
          </div>
        </motion.div>
      )}

      {/* Can't afford overlay */}
      {!canAfford && !isActive && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
          <div className="text-center">
            <div className="text-2xl mb-1">⚠️</div>
            <div className="text-xs text-white/80">Not enough energy</div>
          </div>
        </div>
      )}
    </motion.button>
  );
};

export default ActionCard;
